import { LoaderCircle, Pencil, Save, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';

import type { HistoryDaySummary, UpdateHistoryEntryInput } from '@calorie-tracker/api';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { trpc } from '@/lib/trpc';

import { fieldClassName } from './constants';
import {
  createEditableMacroState,
  formatDateStringForHeading,
  formatMealTime,
  formatMealTypeLabel,
  formatNumber,
  formatSourceLabel,
  getErrorMessage,
  getTodayStatusMeta,
} from './helpers';
import type { AppEntrySummary } from './types';

interface HistoryEntryEditorState {
  calories: string;
  carbsGrams: string;
  fatGrams: string;
  inputQuantityAmount: string;
  inputQuantityUnit: string;
  normalizedName: string;
  proteinGrams: string;
}

export function HistoryScreen({ todayDate }: { todayDate: string }) {
  const utils = trpc.useUtils();
  const historyDays = trpc.history.days.useQuery(
    {
      beforeDate: todayDate,
      limit: 14,
    },
    {
      retry: false,
    },
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [editorState, setEditorState] = useState<HistoryEntryEditorState | null>(null);
  const [historyActionError, setHistoryActionError] = useState<string | null>(null);
  const availableDays = useMemo(() => historyDays.data ?? [], [historyDays.data]);
  const selectedDaySummary = trpc.history.summary.useQuery(
    {
      date: selectedDate ?? todayDate,
    },
    {
      enabled: selectedDate !== null,
      retry: false,
    },
  );
  const updateEntry = trpc.history.updateEntry.useMutation();
  const deleteEntry = trpc.history.deleteEntry.useMutation();

  useEffect(() => {
    if (availableDays.length === 0) {
      setSelectedDate(null);
      setEditingEntryId(null);
      setEditorState(null);
      return;
    }

    const hasSelectedDate =
      selectedDate !== null && availableDays.some((summary) => summary.date === selectedDate);

    if (!hasSelectedDate) {
      setSelectedDate(availableDays[0]?.date ?? null);
    }
  }, [availableDays, selectedDate]);

  useEffect(() => {
    setEditingEntryId(null);
    setEditorState(null);
    setHistoryActionError(null);
  }, [selectedDate]);

  function startEditingEntry(entry: AppEntrySummary) {
    setEditingEntryId(entry.id);
    setEditorState(createEditableMacroState(entry));
    setHistoryActionError(null);
  }

  function updateEditorField<Key extends keyof HistoryEntryEditorState>(
    key: Key,
    value: HistoryEntryEditorState[Key],
  ) {
    setEditorState((currentState) => {
      if (!currentState) {
        return currentState;
      }

      return {
        ...currentState,
        [key]: value,
      };
    });
  }

  async function invalidateHistory(date: string) {
    await Promise.all([
      utils.history.days.invalidate({
        beforeDate: todayDate,
        limit: 14,
      }),
      utils.history.summary.invalidate({ date }),
    ]);
  }

  async function submitEntryEdit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!editingEntryId || !editorState || !selectedDate) {
      return;
    }

    const amount = Number(editorState.inputQuantityAmount);
    const calories = Number(editorState.calories);
    const proteinGrams = Number(editorState.proteinGrams);
    const carbsGrams = Number(editorState.carbsGrams);
    const fatGrams = Number(editorState.fatGrams);

    if (
      !Number.isFinite(amount) ||
      !Number.isFinite(calories) ||
      !Number.isFinite(proteinGrams) ||
      !Number.isFinite(carbsGrams) ||
      !Number.isFinite(fatGrams)
    ) {
      setHistoryActionError('Enter valid numbers before saving changes.');
      return;
    }

    setHistoryActionError(null);

    const input: UpdateHistoryEntryInput = {
      entryId: editingEntryId,
      normalizedName: editorState.normalizedName,
      inputQuantity: {
        amount,
        unit: editorState.inputQuantityUnit,
      },
      macros: {
        calories,
        proteinGrams,
        carbsGrams,
        fatGrams,
      },
    };

    try {
      const result = await updateEntry.mutateAsync(input);
      await invalidateHistory(result.date);
      setEditingEntryId(null);
      setEditorState(null);
    } catch (error) {
      setHistoryActionError(getErrorMessage(error));
    }
  }

  async function removeEntry(entryId: string) {
    if (!selectedDate || !window.confirm('Delete this saved entry from history?')) {
      return;
    }

    setHistoryActionError(null);

    try {
      const result = await deleteEntry.mutateAsync({ entryId });
      await invalidateHistory(result.date);
      setEditingEntryId((currentId) => (currentId === entryId ? null : currentId));
      setEditorState((currentState) => (editingEntryId === entryId ? null : currentState));
    } catch (error) {
      setHistoryActionError(getErrorMessage(error));
    }
  }

  const selectedDay = selectedDaySummary.data;
  const selectedTotals = selectedDay?.totals ?? {
    calories: 0,
    proteinGrams: 0,
    carbsGrams: 0,
    fatGrams: 0,
  };
  const isMutating = updateEntry.isPending || deleteEntry.isPending;
  const selectedStatusMeta = getTodayStatusMeta(selectedDay?.status ?? 'no-goal');

  return (
    <div className="space-y-4">
      <Card className="space-y-4">
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">History</p>
          <h2 className="text-2xl font-semibold">Recent days</h2>
        </div>

        {historyDays.error ? (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {getErrorMessage(historyDays.error)}
          </p>
        ) : null}

        {historyDays.isPending ? (
          <div className="flex items-center gap-3 rounded-md border border-border/70 bg-background px-3 py-4 text-sm text-muted-foreground">
            <LoaderCircle className="h-4 w-4 animate-spin text-primary" />
            Loading previous days…
          </div>
        ) : availableDays.length === 0 ? (
          <div className="border border-dashed border-border bg-background px-3 py-5 text-sm leading-6 text-muted-foreground">
            No past days are saved yet. History fills in after you log meals on earlier dates.
          </div>
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {availableDays.map((day: HistoryDaySummary) => {
              const isSelected = day.date === selectedDate;

              return (
                <button
                  className={`min-w-32 shrink-0 rounded-md border px-3 py-3 text-left transition-colors ${
                    isSelected
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-border/70 bg-background/80 text-foreground'
                  }`}
                  key={day.date}
                  onClick={() => setSelectedDate(day.date)}
                  type="button"
                >
                  <p
                    className={`text-xs ${
                      isSelected ? 'text-background/70' : 'text-muted-foreground'
                    }`}
                  >
                    {formatDateStringForHeading(day.date)}
                  </p>
                  <p className="mt-2 text-xl font-semibold">
                    {formatNumber(day.totals.calories)} cal
                  </p>
                  <p
                    className={`mt-2 text-xs ${
                      isSelected ? 'text-background/70' : 'text-muted-foreground'
                    }`}
                  >
                    {day.entryCount} entr{day.entryCount === 1 ? 'y' : 'ies'} across {day.mealCount}{' '}
                    meal{day.mealCount === 1 ? '' : 's'}
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </Card>

      {selectedDate ? (
        <>
          <Card className="space-y-5">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Selected day</p>
                <div className="flex items-end gap-2">
                  <h3 className="text-4xl font-semibold leading-none">
                    {selectedDay ? formatNumber(selectedTotals.calories) : '...'}
                  </h3>
                  <p className="pb-1 text-sm text-muted-foreground">
                    {selectedDay?.goal
                      ? `/ ${formatNumber(selectedDay.goal.calories)} cal`
                      : 'calories logged'}
                  </p>
                </div>
                <p className="text-sm leading-6 text-muted-foreground">
                  {formatDateStringForHeading(selectedDate)}
                </p>
              </div>
              <span
                className={`rounded px-2 py-1 text-xs font-medium ${selectedStatusMeta.badgeClassName}`}
              >
                {selectedStatusMeta.label}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-3 border-y border-border py-4 text-sm">
              <div>
                <p className="text-muted-foreground">Protein</p>
                <p className="mt-1 text-xl font-semibold">
                  {formatNumber(selectedTotals.proteinGrams)}g
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Carbs</p>
                <p className="mt-1 text-xl font-semibold">
                  {formatNumber(selectedTotals.carbsGrams)}g
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Fat</p>
                <p className="mt-1 text-xl font-semibold">
                  {formatNumber(selectedTotals.fatGrams)}g
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Meals</p>
                <p className="mt-1 text-xl font-semibold">{selectedDay?.meals.length ?? 0}</p>
              </div>
            </div>

            {selectedDaySummary.error ? (
              <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {getErrorMessage(selectedDaySummary.error)}
              </p>
            ) : null}

            {historyActionError ? (
              <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {historyActionError}
              </p>
            ) : null}

            {selectedDay?.reviewCount ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {selectedDay.reviewCount} entr
                {selectedDay.reviewCount === 1 ? 'y still needs' : 'ies still need'} review unless
                you confirm or correct them.
              </p>
            ) : null}
          </Card>

          <Card className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Meals</p>
                <h3 className="mt-1 text-xl font-semibold">
                  {selectedDate ? formatDateStringForHeading(selectedDate) : 'History'}
                </h3>
              </div>
              {selectedDaySummary.isPending ? (
                <LoaderCircle className="h-4 w-4 animate-spin text-primary" />
              ) : null}
            </div>

            {selectedDay && selectedDay.meals.length === 0 ? (
              <div className="border border-dashed border-border bg-background px-3 py-5 text-sm leading-6 text-muted-foreground">
                No saved meals exist for this day.
              </div>
            ) : (
              <div className="divide-y divide-border">
                {selectedDay?.meals.map(({ meal, totals: mealTotals, entries }) => (
                  <section className="space-y-3 py-4 first:pt-0 last:pb-0" key={meal.id}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium">{formatMealTypeLabel(meal.mealType)}</p>
                        <h4 className="mt-1 text-xs text-muted-foreground">
                          {formatMealTime(meal.consumedAt)}
                        </h4>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold">
                          {formatNumber(mealTotals.calories)} cal
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {formatNumber(mealTotals.proteinGrams)}p /{' '}
                          {formatNumber(mealTotals.carbsGrams)}c /{' '}
                          {formatNumber(mealTotals.fatGrams)}f
                        </p>
                      </div>
                    </div>

                    <div className="space-y-1">
                      {entries.map((entry) => {
                        const isEditing = entry.id === editingEntryId;

                        return (
                          <div
                            className="space-y-3 border-t border-border/70 py-3 first:border-t-0"
                            key={entry.id}
                          >
                            {isEditing && editorState ? (
                              <form className="space-y-3" onSubmit={submitEntryEdit}>
                                <label className="grid gap-2 text-sm">
                                  <span className="font-medium">Food</span>
                                  <input
                                    className={fieldClassName}
                                    onChange={(event) =>
                                      updateEditorField('normalizedName', event.target.value)
                                    }
                                    required
                                    type="text"
                                    value={editorState.normalizedName}
                                  />
                                </label>

                                <div className="grid grid-cols-2 gap-3">
                                  <label className="grid gap-2 text-sm">
                                    <span className="font-medium">Amount</span>
                                    <input
                                      className={fieldClassName}
                                      min="0.1"
                                      onChange={(event) =>
                                        updateEditorField('inputQuantityAmount', event.target.value)
                                      }
                                      required
                                      step="0.1"
                                      type="number"
                                      value={editorState.inputQuantityAmount}
                                    />
                                  </label>
                                  <label className="grid gap-2 text-sm">
                                    <span className="font-medium">Unit</span>
                                    <input
                                      className={fieldClassName}
                                      onChange={(event) =>
                                        updateEditorField('inputQuantityUnit', event.target.value)
                                      }
                                      required
                                      type="text"
                                      value={editorState.inputQuantityUnit}
                                    />
                                  </label>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <label className="grid gap-2 text-sm">
                                    <span className="font-medium">Calories</span>
                                    <input
                                      className={fieldClassName}
                                      min="0"
                                      onChange={(event) =>
                                        updateEditorField('calories', event.target.value)
                                      }
                                      required
                                      step="1"
                                      type="number"
                                      value={editorState.calories}
                                    />
                                  </label>
                                  <label className="grid gap-2 text-sm">
                                    <span className="font-medium">Protein (g)</span>
                                    <input
                                      className={fieldClassName}
                                      min="0"
                                      onChange={(event) =>
                                        updateEditorField('proteinGrams', event.target.value)
                                      }
                                      required
                                      step="0.1"
                                      type="number"
                                      value={editorState.proteinGrams}
                                    />
                                  </label>
                                  <label className="grid gap-2 text-sm">
                                    <span className="font-medium">Carbs (g)</span>
                                    <input
                                      className={fieldClassName}
                                      min="0"
                                      onChange={(event) =>
                                        updateEditorField('carbsGrams', event.target.value)
                                      }
                                      required
                                      step="0.1"
                                      type="number"
                                      value={editorState.carbsGrams}
                                    />
                                  </label>
                                  <label className="grid gap-2 text-sm">
                                    <span className="font-medium">Fat (g)</span>
                                    <input
                                      className={fieldClassName}
                                      min="0"
                                      onChange={(event) =>
                                        updateEditorField('fatGrams', event.target.value)
                                      }
                                      required
                                      step="0.1"
                                      type="number"
                                      value={editorState.fatGrams}
                                    />
                                  </label>
                                </div>

                                <div className="flex gap-2">
                                  <Button disabled={isMutating} size="sm" type="submit">
                                    {updateEntry.isPending ? (
                                      <>
                                        <LoaderCircle className="h-4 w-4 animate-spin" />
                                        Saving
                                      </>
                                    ) : (
                                      <>
                                        <Save className="h-4 w-4" />
                                        Save
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    disabled={isMutating}
                                    onClick={() => {
                                      setEditingEntryId(null);
                                      setEditorState(null);
                                      setHistoryActionError(null);
                                    }}
                                    size="sm"
                                    type="button"
                                    variant="outline"
                                  >
                                    <X className="h-4 w-4" />
                                    Cancel
                                  </Button>
                                </div>
                              </form>
                            ) : (
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold">
                                    {entry.normalizedName}
                                  </p>
                                  <p className="mt-1 text-xs text-muted-foreground">
                                    {formatNumber(entry.inputQuantity.amount)}{' '}
                                    {entry.inputQuantity.unit} • {formatSourceLabel(entry.source)}
                                  </p>
                                </div>
                                <div className="shrink-0 text-right">
                                  <p className="text-sm font-semibold">
                                    {formatNumber(entry.macros.calories)} cal
                                  </p>
                                  <p
                                    className={`mt-1 text-xs ${
                                      entry.requiresReview
                                        ? 'text-amber-700'
                                        : 'text-muted-foreground'
                                    }`}
                                  >
                                    {entry.requiresReview ? 'Needs review' : 'Ready'}
                                  </p>
                                </div>
                              </div>
                            )}

                            {!isEditing ? (
                              <div className="flex gap-2">
                                <Button
                                  disabled={isMutating}
                                  onClick={() => startEditingEntry(entry)}
                                  size="sm"
                                  type="button"
                                  variant="outline"
                                >
                                  <Pencil className="h-4 w-4" />
                                  Edit
                                </Button>
                                <Button
                                  className="text-red-700 hover:text-red-700"
                                  disabled={isMutating}
                                  onClick={() => void removeEntry(entry.id)}
                                  size="sm"
                                  type="button"
                                  variant="outline"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  Delete
                                </Button>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </Card>
        </>
      ) : null}
    </div>
  );
}
